import React from "react";
import { useNavigate } from "react-router-dom";

import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import Radio from "@/components/ui/Radio.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import supabase from "@/services/supabaseClient";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import SettingsToggleRow from "@/sot/primitives/SettingsToggleRow.jsx";
import Switch from "@/sot/primitives/Switch.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import {
  runMessageAlertPreview,
} from "@/utils/deviceAlerts.js";
import { canVibrate } from "@/utils/haptics.js";
import {
  DEFAULT_MESSAGE_ALERT_SOUND,
  buildDeviceAlertsPatch,
  mapDbPrefsToUi,
  readNotificationPrefsCache,
  writeNotificationPrefsCache,
} from "@/utils/notificationPreferences.js";
import { patchOwnNotificationPreferences } from "@/services/notificationPreferencesService.js";

const SOUND_OPTIONS = [
  {
    value: "off",
    title: "Off",
    support: "Mute foreground message alert sound.",
  },
  {
    value: "soft",
    title: "Soft",
    support: "Gentle two-note alert.",
  },
  {
    value: "standard",
    title: "Standard",
    support: "Balanced everyday message alert.",
  },
  {
    value: "urgent",
    title: "Urgent",
    support: "Stronger alert for noisy surroundings.",
  },
];

function isFrenchLanguageCode(value = "") {
  return String(value || "").toLowerCase().startsWith("fr");
}

const FRENCH_SOUND_OPTIONS = {
  off: {
    title: "Désactivé",
    support: "Couper le son des alertes de message au premier plan.",
  },
  soft: {
    title: "Doux",
    support: "Alerte discrète à deux notes.",
  },
  standard: {
    title: "Standard",
    support: "Alerte équilibrée pour l’usage quotidien.",
  },
  urgent: {
    title: "Urgent",
    support: "Alerte plus forte pour les environnements bruyants.",
  },
};

function isMissingPrefsColumn(error) {
  const msg = String(error?.message || "").toLowerCase();
  return msg.includes("notification_prefs") && msg.includes("column");
}

function ChoiceRow({
  title,
  support,
  checked = false,
  divider = true,
  onClick,
}) {
  return (
    <>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center justify-between px-4 py-4 text-left active:scale-[0.998] transition"
      >
        <div className="min-w-0">
          <div className="text-body-large text-on-surface">{title}</div>
          {support ? (
            <div className="mt-0.5 text-body-medium text-on-surface-var">
              {support}
            </div>
          ) : null}
        </div>
        <div className="ml-3 shrink-0">
          <Radio checked={checked} variant="afrimed" readOnly />
        </div>
      </button>
      {divider ? <div className="ml-4 h-px bg-[rgba(230,231,235,1)]" /> : null}
    </>
  );
}

export default function AlertsAndSoundsPreferencesCommon({ role = "patient" }) {
  const navigate = useNavigate();
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const { languageCode, t } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  const copy = React.useCallback(
    (key, fr, en) => (isFrench ? fr : t(key, en)),
    [isFrench, t]
  );
  const userId = String(auth?.user?.id || "").trim();
  const basePath = `/settings/${role === "doctor" ? "doctor" : "patient"}`;
  const profileTable = role === "doctor" ? "doctor_profiles" : "patient_profiles";
  const [prefs, setPrefs] = React.useState(() =>
    mapDbPrefsToUi(readNotificationPrefsCache(role, userId), role)
  );
  const prefsRef = React.useRef(prefs);
  const [prefsRevision, setPrefsRevision] = React.useState(null);
  const [hydrated, setHydrated] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState("");
  const [testing, setTesting] = React.useState(false);
  const vibrationSupported = canVibrate();

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const {
          data: { user },
          error: authErr,
        } = await supabase.auth.getUser();

        if (authErr || !user || !alive) {
          if (alive) setHydrated(true);
          return;
        }

        const { data, error } = await supabase
          .from(profileTable)
          .select("notification_prefs, updated_at")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) {
          if (!isMissingPrefsColumn(error)) {
            console.error("Alerts & Sounds hydrate failed", error);
          }
          if (alive) setHydrated(true);
          return;
        }

        if (!alive) return;

        const fromDb = mapDbPrefsToUi(data?.notification_prefs, role);
        prefsRef.current = fromDb;
        setPrefs(fromDb);
        setPrefsRevision(data?.updated_at || null);
        writeNotificationPrefsCache(role, user.id, fromDb);
      } finally {
        if (alive) setHydrated(true);
      }
    })();

    return () => {
      alive = false;
    };
  }, [profileTable, role]);

  React.useEffect(() => {
    prefsRef.current = prefs;
    writeNotificationPrefsCache(role, userId, prefs);
  }, [prefs, role, userId]);

  const applyDeviceAlertIntent = React.useCallback(
    async (nextPrefs) => {
      prefsRef.current = nextPrefs;
      setPrefs(nextPrefs);
      writeNotificationPrefsCache(role, userId, nextPrefs);

      const { data, error } = await patchOwnNotificationPreferences({
        role,
        expectedUpdatedAt: prefsRevision,
        patch: { device_alerts: buildDeviceAlertsPatch(nextPrefs, role) },
        source: "alerts_and_sounds",
      });
      if (error) {
        console.error("Alerts & Sounds intent save failed", error);
        return false;
      }
      if (data?.notification_prefs) {
        const persisted = mapDbPrefsToUi(data.notification_prefs, role);
        prefsRef.current = persisted;
        setPrefs(persisted);
        setPrefsRevision(data.updated_at || null);
        writeNotificationPrefsCache(role, userId, persisted);
      }
      return true;
    },
    [prefsRevision, role, userId],
  );

  React.useEffect(() => {
    if (!snackbar) return undefined;
    const timer = window.setTimeout(() => setSnackbar(""), 2600);
    return () => window.clearTimeout(timer);
  }, [snackbar]);

  const soundProfile = String(
    prefs.messageAlertSound || DEFAULT_MESSAGE_ALERT_SOUND
  ).trim().toLowerCase();

  const setSoundProfile = React.useCallback((value) => {
    const nextPrefs = {
      ...prefsRef.current,
      soundOn: value !== "off",
      messageAlertSound: value,
    };
    void applyDeviceAlertIntent(nextPrefs);
    if (value === "off") {
      setSnackbar(isFrench ? "Son d’alerte coupé" : "Alert sound muted");
      return;
    }
    if (value === "soft") {
      setSnackbar(isFrench ? "Alerte douce activée" : "Soft alert enabled");
      return;
    }
    if (value === "standard") {
      setSnackbar(isFrench ? "Alerte standard activée" : "Standard alert enabled");
      return;
    }
    setSnackbar(isFrench ? "Alerte urgente activée" : "Urgent alert enabled");
  }, [applyDeviceAlertIntent, isFrench]);

  const handleTestAlert = React.useCallback(async () => {
    setTesting(true);
    try {
      const result = await runMessageAlertPreview({
        soundProfile,
        vibrationOn: Boolean(prefs.vibrationOn),
      });

      if (result.soundPlayed || result.vibrationTriggered) {
        setSnackbar(isFrench ? "Alerte de test émise" : "Test alert played");
        return;
      }

      if (soundProfile === "off" && !prefs.vibrationOn) {
        setSnackbar(isFrench ? "Activez le son ou la vibration pour tester les alertes" : "Enable sound or vibration to test alerts");
        return;
      }

      setSnackbar(isFrench ? "Impossible d’émettre l’alerte de test pour le moment" : "Test alert could not play right now");
    } finally {
      setTesting(false);
    }
  }, [isFrench, prefs.vibrationOn, soundProfile]);

  return (
    <SettingsScreenShell>
      <TopBarCenter
        close
        onBack={() => navigate(`${basePath}/preferences`)}
        title={copy("settings.preferences.alertsSounds.title", "Alertes et sons", "Alerts & Sounds")}
      />

      <div className="mt-6 space-y-8">
        <section className="space-y-3">
          <h2 className="text-title-small text-on-surface-var">
            {copy("settings.preferences.alertsSounds.sound.heading", "Son des alertes de message", "Message alert sound")}
          </h2>
          <p className="px-0.5 text-body-small text-on-surface-var">
            {copy(
              "settings.preferences.alertsSounds.sound.copy",
              "Choisissez le son des nouvelles alertes de message lorsque vous êtes actif dans AfriMed™.",
              "Choose how new message alerts sound while you are active in AfriMed™.",
            )}
          </p>

          <Card className="overflow-hidden">
            {SOUND_OPTIONS.map((option, index) => (
              <ChoiceRow
                key={option.value}
                title={copy(
                  `settings.preferences.alertsSounds.sound.options.${option.value}.title`,
                  FRENCH_SOUND_OPTIONS[option.value]?.title || option.title,
                  option.title
                )}
                support={copy(
                  `settings.preferences.alertsSounds.sound.options.${option.value}.support`,
                  FRENCH_SOUND_OPTIONS[option.value]?.support || option.support,
                  option.support
                )}
                checked={soundProfile === option.value}
                onClick={() => setSoundProfile(option.value)}
                divider={index !== SOUND_OPTIONS.length - 1}
              />
            ))}
          </Card>
        </section>

        <section className="space-y-3">
          <h2 className="text-title-small text-on-surface-var">
            {copy("settings.preferences.alertsSounds.vibration.heading", "Vibration", "Vibrate")}
          </h2>

          <Card className="overflow-hidden">
            <SettingsToggleRow
              title={copy("settings.preferences.alertsSounds.vibration.title", "Vibrer lors des nouveaux messages", "Vibrate on new messages")}
              support={copy(
                "settings.preferences.alertsSounds.vibration.support",
                "Utilise la vibration AfriMed™ lorsque votre navigateur et votre appareil le permettent.",
                "Uses AfriMed™ vibration when your browser and device allow it.",
              )}
              trailing={
                <Switch
                  checked={Boolean(prefs.vibrationOn)}
                  onChange={(next) => {
                    const on =
                      typeof next === "boolean"
                        ? next
                        : Boolean(next?.target?.checked);
                    void applyDeviceAlertIntent({
                      ...prefsRef.current,
                      vibrationOn: on,
                    });
                    setSnackbar(
                      on
                        ? isFrench ? "Vibration activée" : "Vibration enabled"
                        : isFrench ? "Vibration désactivée" : "Vibration disabled"
                    );
                  }}
                />
              }
              divider={false}
            />
          </Card>
        </section>

        <section className="space-y-3">
          <h2 className="text-title-small text-on-surface-var">
            {copy("settings.preferences.alertsSounds.test.heading", "Tester l’alerte", "Test alert")}
          </h2>

          <div className="space-y-3 px-0.5">
            <p className="text-body-small text-on-surface-var">
              {copy(
                "settings.preferences.alertsSounds.helper.sound",
                "Le son fonctionne uniquement lorsque AfriMed™ est actif dans votre session de navigateur.",
                "Sound works only while AfriMed™ is active in your browser session.",
              )}
            </p>
            <p className="text-body-small text-on-surface-var">
              {copy(
                "settings.preferences.alertsSounds.helper.vibration",
                "La vibration fonctionne sur les appareils et navigateurs compatibles, y compris Android Web. Certains appareils ou navigateurs peuvent ne pas vibrer.",
                "Vibration works on supported devices and browsers, including Android web. Some devices and browsers may not vibrate.",
              )}
            </p>
            <div className="pt-1">
              <Button
                variant="tonal"
                fullWidth
                loading={testing}
                onClick={handleTestAlert}
              >
                {copy("settings.preferences.alertsSounds.test.action", "Tester l’alerte", "Test alert")}
              </Button>
            </div>
            {!vibrationSupported ? (
              <p className="text-body-small text-on-surface-var">
                {copy(
                  "settings.preferences.alertsSounds.test.browserHint",
                  "La vibration n’est pas disponible sur cet appareil ou ce navigateur pour le moment.",
                  "Vibration is not available on this device or browser right now.",
                )}
              </p>
            ) : null}
          </div>
        </section>
      </div>

      <Snackbar
        open={Boolean(snackbar)}
        message={snackbar}
        onClose={() => setSnackbar("")}
      />
    </SettingsScreenShell>
  );
}
